import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Zap, FileCode, Shield, Coins } from 'lucide-react';
import { usePrefabStore } from '../../stores/usePrefabStore';
import { PREFAB_CATEGORY_LABELS } from '../../types/prefab';
import { FeeBreakdownPanel } from '../panels/FeeBreakdownPanel';
import { estimatePrefabFees, getActionFeeRange } from '../../editor/feeEstimator';
import s from './Pages.module.css';

export function PrefabDetailPage() {
  const { prefabId } = useParams<{ prefabId: string }>();
  const navigate = useNavigate();
  const catalog = usePrefabStore(p => p.catalog);
  const fetchCatalog = usePrefabStore(p => p.fetchCatalog);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const prefab = catalog.find(p => p.id === prefabId);

  // Must call useMemo unconditionally (Rules of Hooks) — guard against undefined inside
  const feeSummary = useMemo(
    () => (prefab ? estimatePrefabFees(prefab) : null),
    [prefab]
  );

  if (!prefab) {
    return (
      <div className={s.page}>
        <div className={s.emptyState}>
          <div className={s.emptyTitle}>Prefab not found</div>
          <div className={s.emptyDesc}>The requested prefab does not exist.</div>
          <button className={s.btnPrimary} onClick={() => navigate('/prefabs')}>
            Back to Prefabs
          </button>
        </div>
      </div>
    );
  }

  const mutatingActions = prefab.actions.filter(a => a.mutates);
  const readActions = prefab.actions.filter(a => !a.mutates);

  return (
    <div className={s.page}>
      {/* Back navigation */}
      <button className={s.btnGhost} onClick={() => navigate('/prefabs')} style={{ marginBottom: 'var(--spacing-lg)' }}>
        <ArrowLeft size={16} /> Back to Prefabs
      </button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--spacing-2xl)', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <h1 className={s.pageTitle} style={{ marginBottom: 0 }}>{prefab.name}</h1>
          </div>
          <p className={s.pageSubtitle} style={{ maxWidth: 600 }}>{prefab.description}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <span className={`${s.badge} ${s.badgeInfo}`}>{PREFAB_CATEGORY_LABELS[prefab.category]}</span>
            <span className={`${s.badge} ${s.badgeNeutral}`}>{prefab.kind}</span>
            <span className={`${s.badge} ${s.badgeNeutral}`}>v{prefab.version}</span>
            {prefab.microSafe && <span className={`${s.badge} ${s.badgeSuccess}`}><Shield size={10} /> Micro-safe</span>}
            {prefab.casualSafe && <span className={`${s.badge} ${s.badgeSuccess}`}>Casual-safe</span>}
          </div>
          {prefab.tags.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {prefab.tags.map(tag => (
                <span key={tag} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--color-bg-tertiary)', color: 'var(--color-text-tertiary)' }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <button className={s.btnPrimary} onClick={() => navigate(`/prefabs/${prefab.id}/deploy`)}>
          <Plus size={16} /> Add to Project
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-xl)' }}>
        {/* Actions */}
        <div>
          <div className={s.sectionHeader}>
            <h2 className={s.sectionTitle}><Zap size={16} style={{ marginRight: 6, verticalAlign: -2 }} />Actions ({prefab.actions.length})</h2>
          </div>
          <div className={`${s.card} ${s.cardStatic}`}>
            {mutatingActions.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-tertiary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Write Actions</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px 0' }}>
                  {mutatingActions.map((action, i) => {
                    const feeRange = getActionFeeRange(action.intentClass);
                    return (
                    <li key={action.name} style={{ padding: '8px 0', borderBottom: i < mutatingActions.length - 1 ? '1px solid var(--color-border)' : 'none', fontSize: 13 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <code style={{ fontWeight: 600, color: 'var(--color-accent)' }}>{action.name}</code>
                        <span className={`${s.badge} ${s.badgeWarning}`} style={{ fontSize: 10 }}>mutates</span>
                        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-secondary)' }}>
                          {feeRange.min_display}–{feeRange.max_display}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                        {action.description} — {action.parameters.length} param{action.parameters.length !== 1 ? 's' : ''}
                      </div>
                    </li>
                    );
                  })}
                </ul>
              </>
            )}
            {readActions.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-tertiary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Read Actions</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {readActions.map((action, i) => {
                    const feeRange = getActionFeeRange(action.intentClass);
                    return (
                    <li key={action.name} style={{ padding: '8px 0', borderBottom: i < readActions.length - 1 ? '1px solid var(--color-border)' : 'none', fontSize: 13 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <code style={{ fontWeight: 500, color: 'var(--color-text-secondary)' }}>{action.name}</code>
                        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                          {feeRange.min_display}–{feeRange.max_display}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                        {action.description} — {action.parameters.length} param{action.parameters.length !== 1 ? 's' : ''}
                      </div>
                    </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        </div>

        {/* Configuration Parameters */}
        <div>
          <div className={s.sectionHeader}>
            <h2 className={s.sectionTitle}>Init Parameters</h2>
          </div>
          <div className={`${s.card} ${s.cardStatic}`}>
            {prefab.params.length === 0 ? (
              <div style={{ color: 'var(--color-text-tertiary)', fontSize: 13 }}>No configuration required.</div>
            ) : (
              <table className={s.table} style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Default</th>
                  </tr>
                </thead>
                <tbody>
                  {prefab.params.map(p => (
                    <tr key={p.key}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{p.label}
                          {p.required && <span style={{ color: 'var(--color-danger)', marginLeft: 4, fontSize: 10 }}>required</span>}
                        </div>
                        {p.description && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{p.description}</div>}
                        {p.enumValues && p.enumLabels && (
                          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                            Options: {p.enumLabels.join(' · ')}
                          </div>
                        )}
                        {p.minimum !== undefined && p.maximum !== undefined && !p.enumValues && (
                          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                            Range: {p.minimum} – {p.maximum}
                          </div>
                        )}
                      </td>
                      <td><span className={`${s.badge} ${s.badgeNeutral}`}>{p.uiWidget === 'addressPicker' ? 'address' : p.type}</span></td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                        {p.defaultValue !== undefined ? String(p.defaultValue) : <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Fee Summary */}
        <div style={{ gridColumn: '1 / -1' }}>
          <div className={s.sectionHeader}>
            <h2 className={s.sectionTitle}><Coins size={16} style={{ marginRight: 6, verticalAlign: -2 }} />Fees</h2>
          </div>
          <div className={`${s.card} ${s.cardStatic}`}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Registration</div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--color-text-primary)' }}>$1.00</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>One-time fee to register this module</div>
            </div>
          </div>

          <FeeBreakdownPanel summary={feeSummary} />
        </div>
      </div>

      {/* Source File */}
      <div style={{ marginTop: 'var(--spacing-2xl)' }}>
        <div className={s.sectionHeader}>
          <h2 className={s.sectionTitle}><FileCode size={16} style={{ marginRight: 6, verticalAlign: -2 }} />Source</h2>
        </div>
        <div className={`${s.card} ${s.cardStatic}`}>
          <table className={s.table} style={{ fontSize: 12 }}>
            <tbody>
              <tr>
                <td style={{ fontWeight: 500, width: '30%' }}>Source File</td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{prefab.sourceFile}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Source Hash</td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, wordBreak: 'break-all' }}>{prefab.sourceHash}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* CTA */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--spacing-3xl)' }}>
        <button className={s.btnPrimary} style={{ padding: '12px 32px', fontSize: 15 }} onClick={() => navigate(`/prefabs/${prefab.id}/deploy`)}>
          <Plus size={18} /> Configure & Add to Project
        </button>
      </div>
    </div>
  );
}
